/**
 * TTL cache para endpoints de catálogo (task_status, task_types, criticalities, roles, etc.).
 * Evita que componentes re-fetcheen datos estáticos en cada montaje.
 * Mismo patrón que serviceInfoApi.js y el decorador @cached del backend.
 */

/** @type {Map<string, { data: unknown, expiresAt: number }>} */
const _cache = new Map();

/** @type {Map<string, Promise<unknown>>} */
const _inFlight = new Map();

/**
 * Fetch con TTL y deduplicación de in-flight para endpoints de catálogo.
 * @param {string} url - URL completa del endpoint
 * @param {number} [ttlMs=300_000] - TTL en ms (default 5 min)
 * @returns {Promise<unknown>}
 */
export async function fetchCatalogCached(url, ttlMs = 300_000) {
  const now = Date.now();
  const entry = _cache.get(url);
  if (entry && now < entry.expiresAt) return entry.data;

  const existing = _inFlight.get(url);
  if (existing) return existing;

  const p = fetch(url, { method: 'GET', credentials: 'include' })
    .then((r) => {
      if (!r.ok) return Promise.reject(new Error(`HTTP ${r.status}`));
      return r.json();
    })
    .then((data) => {
      _cache.set(url, { data, expiresAt: Date.now() + ttlMs });
      return data;
    })
    .finally(() => _inFlight.delete(url));

  _inFlight.set(url, p);
  return p;
}

/**
 * Invalida una URL concreta del cache (útil tras crear/editar un catálogo).
 * @param {string} url
 */
export function invalidateCatalogUrl(url) {
  _cache.delete(url);
  _inFlight.delete(url);
}

/**
 * Limpia todo el cache de catálogos (llamar al cerrar sesión).
 */
export function clearCatalogCache() {
  _cache.clear();
  _inFlight.clear();
}
